import { useState } from "react";
import { useParams, Link } from "react-router";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Skeleton,
} from "@heroui/react";
import { ArrowLeft, Package } from "lucide-react";
import { Button as DialogButton } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useCustomerOrdersV2,
  getOrderStatus,
  getRecipientFromOrder,
} from "@/api/use-customer-orders-v2";
import { ShipmentStatusBadge } from "@/components/shipment-status-badge";
import type { Order, ShipmentPackage } from "@/api.types.v2";
import { useTranslation } from "react-i18next";
import { useOrderPayments } from "@/api/use-order-payments";
import { useRequestCancellation } from "@/api/use-request-cancellation";
import { toast } from "@/hooks/use-toast";
import { PayOrderDialog } from "./pay-order-dialog";
import { CancellationPendingNotice } from "./cancellation-pending-notice";
import {
  canRequestCustomerShipmentCancellation,
  isPreCollectionShipment,
} from "@/api/shipment-action-eligibility";
import {
  getReturnAmountDue,
  ReturnRefundBreakdown,
} from "./return-refund-breakdown";

import { hasOrderRefund, OrderPaymentSummary } from "./order-payment-summary";

function formatCurrency(cents?: number): string {
  if (cents === undefined || cents === null) return "—";
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);
}

function formatCarrier(code?: string): string {
  if (!code) return "";
  return code
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getSenderFromOrder(order: Order) {
  if (!order.shipments?.length) return null;
  const shipment = order.shipments[0];
  const fromAddress = shipment.addresses?.find((a) => a.type === "FROM");
  if (!fromAddress) return null;

  const addr = fromAddress.address;
  const parts = [
    addr?.line1,
    addr?.line2,
    addr?.city,
    addr?.province,
    addr?.postalCode,
  ].filter(Boolean);

  return {
    name: fromAddress.contactName ?? fromAddress.name ?? "",
    fullAddress: parts.join(", "),
    phone: fromAddress.contactPhone ?? fromAddress.phone,
    email: fromAddress.contactEmail ?? fromAddress.email,
  };
}

export default function OrderDetailsScreen() {
  const { t, i18n } = useTranslation("app");
  const { orderId } = useParams<{ orderId: string }>();
  const [returnTransportToPay, setReturnTransportToPay] = useState<
    string | null
  >(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const { data: response, isLoading } = useCustomerOrdersV2({
    page: 1,
    limit: 100,
  });
  const { data: payments = [] } = useOrderPayments(orderId);
  const requestCancellation = useRequestCancellation({
    onSuccess: (data) => {
      if (!data.success) {
        toast({
          variant: "destructive",
          title: t("orders.toastCancellationError"),
          description: data.message,
        });
        return;
      }
      toast({
        title: t("orders.toastCancellationTitle"),
        description: data.message,
      });
      setCancelDialogOpen(false);
    },
    onError: () =>
      toast({
        variant: "destructive",
        title: t("orders.toastCancellationError"),
      }),
  });

  const order = response?.data?.find((o) => o.id === orderId);

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto space-y-4">
        <Skeleton className="h-8 w-32 rounded-medium" />
        <Card shadow="none" className="border border-divider">
          <CardBody className="p-6 space-y-4">
            <Skeleton className="h-6 w-48 rounded-medium" />
            <Skeleton className="h-4 w-full rounded-medium" />
            <Skeleton className="h-4 w-3/4 rounded-medium" />
          </CardBody>
        </Card>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card shadow="none" className="border border-divider">
          <CardBody className="py-12 text-center">
            <Package className="h-12 w-12 text-default-300 mx-auto mb-3" />
            <p className="font-medium text-default-700">
              {t("orderDetails.notFoundTitle")}
            </p>
            <p className="text-small text-default-500 mt-1">
              {t("orderDetails.notFoundBody")}
            </p>
            <Button
              as={Link}
              to="/orders"
              variant="bordered"
              size="sm"
              startContent={<ArrowLeft className="h-4 w-4" />}
              className="mt-4"
            >
              {t("orderDetails.backToOrders")}
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  const status = getOrderStatus(order);
  const sender = getSenderFromOrder(order);
  const recipient = getRecipientFromOrder(order);
  const shipment = order.shipments?.[0];
  const packages = shipment?.packages ?? [];
  const isPaid = order.paymentStatus === "PAID";
  const hasTax = order.taxCents != null && order.taxCents > 0;
  const hasPendingCancellation = order.shipments?.some(
    (item) => item.cancellationRequestedAt
  );
  const showCancel =
    !hasPendingCancellation && canRequestCustomerShipmentCancellation(status);
  const returnTransports =
    order.shipments?.flatMap((item) => item.returnTransports ?? []) ?? [];
  const selectedReturnTransport = returnTransports.find(
    (transport) => transport.id === returnTransportToPay
  );
  const amountDueToPay = selectedReturnTransport
    ? getReturnAmountDue(selectedReturnTransport, payments)
    : 0;

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <Link
        to="/orders"
        className="inline-flex items-center gap-1 text-small text-default-500 hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("orderDetails.backToOrders")}
      </Link>

      {hasPendingCancellation && <CancellationPendingNotice />}

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            SHP-{shipment?.shipmentNumber ?? "…"}
          </h1>
          <p className="text-small text-default-500">
            {new Date(order.createdAt).toLocaleDateString(
              i18n.language === "zh" ? "zh-CN" : "en-CA",
              {
                weekday: "long",
                month: "short",
                day: "numeric",
                year: "numeric",
              }
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ShipmentStatusBadge
            status={status}
            paymentStatus={order.paymentStatus}
          />
          {isPaid && (
            <Chip color="success" variant="flat" size="sm">
              {t("orderDetails.paid")}
            </Chip>
          )}
          {showCancel && (
            <DialogButton
              size="sm"
              variant="destructive"
              onClick={() => setCancelDialogOpen(true)}
            >
              {t(
                isPreCollectionShipment(status)
                  ? "actions.cancel"
                  : "orders.requestCancellation"
              )}
            </DialogButton>
          )}
        </div>
      </div>

      <Card shadow="none" className="border border-divider">
        <CardBody className="py-4 space-y-1">
          {hasTax && (
            <>
              <div className="flex justify-between items-center text-small text-default-500">
                <span>{t("orderDetails.subtotal")}</span>
                <span>
                  {formatCurrency(order.subtotalCents ?? order.totalCents)}
                </span>
              </div>
              <div className="flex justify-between items-center text-small text-default-500">
                <span>{t("orderDetails.tax")}</span>
                <span>{formatCurrency(order.taxCents)}</span>
              </div>
            </>
          )}
          <div className="flex justify-between items-center pt-1">
            <span className="text-default-500">
              {t(
                hasOrderRefund(order)
                  ? "orders.refundSummary.orderTotal"
                  : "orderDetails.total"
              )}
            </span>
            <span className="text-2xl font-bold tracking-tight">
              {formatCurrency(order.totalCents)}
            </span>
          </div>
          <OrderPaymentSummary order={order} showBreakdown />
        </CardBody>
      </Card>

      {returnTransports.map((transport) => (
        <ReturnRefundBreakdown
          key={transport.id}
          transport={transport}
          payments={payments}
          onPayAmountDue={() => setReturnTransportToPay(transport.id)}
        />
      ))}

      {shipment?.trackingNumber && (
        <Card shadow="none" className="border border-divider">
          <CardHeader className="pb-0">
            <span className="text-tiny font-semibold uppercase tracking-wider text-default-500">
              {t("orderDetails.tracking")}
            </span>
          </CardHeader>
          <CardBody>
            <a
              href={`/tracking/${shipment.trackingNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline font-mono text-lg"
            >
              {shipment.trackingNumber}
            </a>
          </CardBody>
        </Card>
      )}

      {shipment?.carrierCode && (
        <Card shadow="none" className="border border-divider">
          <CardHeader className="pb-0">
            <span className="text-tiny font-semibold uppercase tracking-wider text-default-500">
              {t("orderDetails.carrier")}
            </span>
          </CardHeader>
          <CardBody>
            <p>{formatCarrier(shipment.carrierCode)}</p>
          </CardBody>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <Card shadow="none" className="border border-divider">
          <CardHeader className="pb-0">
            <div className="flex items-center gap-2">
              <Chip size="sm" variant="bordered" color="default">
                {t("orderDetails.from")}
              </Chip>
              <span className="text-tiny font-semibold uppercase tracking-wider text-default-500">
                {t("orderDetails.sender")}
              </span>
            </div>
          </CardHeader>
          <CardBody className="space-y-1">
            <p className="font-medium">{sender?.name ?? "—"}</p>
            {sender?.fullAddress && (
              <p className="text-small text-default-500">
                {sender.fullAddress}
              </p>
            )}
            {sender?.phone && (
              <p className="text-small text-default-500">{sender.phone}</p>
            )}
            {sender?.email && (
              <p className="text-small text-default-500">{sender.email}</p>
            )}
          </CardBody>
        </Card>

        <Card shadow="none" className="border border-divider">
          <CardHeader className="pb-0">
            <div className="flex items-center gap-2">
              <Chip size="sm" color="primary" variant="flat">
                {t("orderDetails.to")}
              </Chip>
              <span className="text-tiny font-semibold uppercase tracking-wider text-default-500">
                {t("orderDetails.recipient")}
              </span>
            </div>
          </CardHeader>
          <CardBody className="space-y-1">
            <p className="font-medium">{recipient?.name ?? "—"}</p>
            {recipient?.fullAddress && (
              <p className="text-small text-default-500">
                {recipient.fullAddress}
              </p>
            )}
            {recipient?.phone && (
              <p className="text-small text-default-500">{recipient.phone}</p>
            )}
            {recipient?.email && (
              <p className="text-small text-default-500">{recipient.email}</p>
            )}
          </CardBody>
        </Card>
      </div>

      {packages.length > 0 && (
        <Card shadow="none" className="border border-divider">
          <CardHeader className="pb-0">
            <span className="text-tiny font-semibold uppercase tracking-wider text-default-500">
              {t("orderDetails.packagesCount", { count: packages.length })}
            </span>
          </CardHeader>
          <CardBody>
            <div className="space-y-2">
              {packages.map((pkg: ShipmentPackage, index: number) => (
                <div
                  key={pkg.id ?? index}
                  className="rounded-medium bg-default-50 p-3"
                >
                  <p className="font-medium">
                    {t("orderDetails.package", { number: index + 1 })}
                  </p>
                  <p className="text-small text-default-500">
                    {pkg.weightGrams
                      ? `${(pkg.weightGrams / 1000).toFixed(1)} kg`
                      : "—"}
                    {pkg.lengthCm &&
                      pkg.widthCm &&
                      pkg.heightCm &&
                      ` · ${pkg.lengthCm} × ${pkg.widthCm} × ${pkg.heightCm} cm`}
                  </p>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}
      {amountDueToPay > 0 && (
        <PayOrderDialog
          open
          onOpenChange={(open) => !open && setReturnTransportToPay(null)}
          orderId={order.id}
          totalCents={amountDueToPay}
          orderNumber={`ORD-${order.orderNumber}`}
          allowInvoice={false}
          returnTransport={selectedReturnTransport}
          purpose="RETURN"
        />
      )}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("orders.cancelModalTitle")}</DialogTitle>
            <DialogDescription>{t("orders.cancelModalBody")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogButton
              variant="outline"
              onClick={() => setCancelDialogOpen(false)}
            >
              {t("orders.keepOrder")}
            </DialogButton>
            <DialogButton
              variant="destructive"
              onClick={() => requestCancellation.mutate({ orderId: order.id })}
              disabled={requestCancellation.isPending}
              aria-busy={requestCancellation.isPending}
            >
              {requestCancellation.isPending
                ? t("orders.submitting")
                : t("orders.requestCancellation")}
            </DialogButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
